import { useCallback, useState } from "react";
import { LogoIcon } from "@/components/brand/logo-icon";

export function HeroBeam({ className }: { className?: string }) {
	const [visible, setVisible] = useState(false);
	const [runId, setRunId] = useState(0);
	const ref = useCallback((node: HTMLDivElement | null) => {
		if (!node) return;
		let wasVisible = false;
		const replay = () => setRunId((id) => id + 1);
		const cleanups: Array<() => void> = [];

		if (typeof IntersectionObserver === "undefined") {
			setVisible(true);
			wasVisible = true;
			replay();
		} else {
			const observer = new IntersectionObserver(
				([entry]) => {
					const isVisible = Boolean(entry?.isIntersecting);
					setVisible(isVisible);
					if (isVisible && !wasVisible) replay();
					wasVisible = isVisible;
				},
				{ threshold: 0 },
			);
			observer.observe(node);
			cleanups.push(() => observer.disconnect());
		}

		const onRefocus = () => {
			if (document.visibilityState !== "visible") return;
			if (wasVisible) replay();
		};
		document.addEventListener("visibilitychange", onRefocus);
		window.addEventListener("focus", onRefocus);
		cleanups.push(() => {
			document.removeEventListener("visibilitychange", onRefocus);
			window.removeEventListener("focus", onRefocus);
		});

		return () => {
			for (const fn of cleanups) fn();
		};
	}, []);

	return (
		<div
			ref={ref}
			data-animate={visible ? "true" : undefined}
			className={`relative flex items-center justify-center ${className ?? ""}`}
		>
			{/* Radial glow */}
			<div
				className="pointer-events-none absolute inset-0 -z-10"
				aria-hidden="true"
				style={{
					background:
						"radial-gradient(ellipse 60% 60% at 50% 50%, rgba(255,255,255,0.18) 0%, transparent 80%)",
				}}
			/>
			<LogoIcon
				key={runId}
				animateDraw
				className="size-24 rounded-[22.5%]"
				style={{ boxShadow: "rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px, rgba(0,0,0,0.04) 0px 8px 8px -8px" }}
			/>
		</div>
	);
}
